import React, {Dispatch, SetStateAction} from 'react';
import {TouchableOpacity, View} from 'react-native';

import {GetHolderProfileCabinetResponsePayload} from '../../../../../shared/models/api';
import {useStyles as useStylesHook} from '../../../../../shared/hooks/styles';
import {Sex} from '../../../../../shared/models/profile/sex';
import {formatSnils} from '../../../../../shared/pipes';
import {HolderAvatar} from '../../../../../shared/components/holderAvatar';
import {Text} from '../../../../../shared/components/text';
import {images} from '../../../../../assets/icons';
import {useTheme} from '../../../../../shared/theme';
import {maskPrivateData, PrivateDataMaskType} from '../../../../../shared/pipes/maskPrivateData';
import {useScreenState} from '../../../../../shared/hooks/useScreenState';
import {datePipe} from '../../../../../shared/pipes/date';
import {LinkedAccounts} from '../linkedAccounts';
import {NameInfoDesktop} from '../nameInfoDesktop';
import {LoadableContent} from '../../../../../shared/components/loadableContent';
import {useAccountLinker} from '../../../../../shared/hooks/useAccountLinkerHook/useAccountLinker';

interface DataVisibilitySwitchProps {
  isMasked: boolean;
  onChange: Dispatch<SetStateAction<boolean>>;
}

interface InfoItemProps {
  testID: string;
  title: string;
  value?: string;
}

interface Props {
  profile: GetHolderProfileCabinetResponsePayload | null;
}

const DataVisibilitySwitch = ({isMasked, onChange}: DataVisibilitySwitchProps) => {
  const theme = useTheme();
  const styles = useStylesHook(() => {
    return {
      container: {display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginTop: 5},
      switch: {display: 'flex', flexDirection: 'row', alignItems: 'center'},
      switchLabel: {
        marginLeft: 10,
        ...theme.fonts.bodySmall,
        textTransform: 'uppercase',
        color: theme.color.primaryColor,
      },
    };
  });
  return (
    <View style={styles.container}>
      <TouchableOpacity testID='data-visibility-button-id' style={styles.switch} onPress={() => onChange(!isMasked)}>
        {isMasked ? (
          <images.EyeOffFat color={theme.color.primaryColor} />
        ) : (
          <images.EyeFat color={theme.color.primaryColor} />
        )}
        <Text testID='change-visibility-profile-card-info-id' style={styles.switchLabel}>
          {isMasked ? 'Показать данные' : 'Скрыть данные'}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const InfoItem = (props: InfoItemProps) => {
  const styles = useStylesHook(({theme}) => {
    return {
      container: {display: 'flex', flexDirection: 'column', marginTop: 20},
      h2: {
        ...theme.fonts.h2,
      },
      title: {
        ...theme.fonts.body,
        color: theme.color.labelText,
      },
      value: {
        ...theme.fonts.h5Light,
        marginTop: 2,
      },
    };
  });
  if (props.value === null || props.value === undefined || props.value === '') {
    return null;
  }
  return (
    <View testID={props.testID} style={styles.container}>
      <Text testID={props.testID + '-title'} style={styles.title}>
        {props.title}
      </Text>
      <Text testID={props.testID + '-value'} style={styles.value}>
        {props.value}
      </Text>
    </View>
  );
};

export default (props: Props) => {
  const {profile} = props;
  const [isMasked, setMasked] = useScreenState(true);
  const {isLoading} = useAccountLinker();
  const styles = useStylesHook(({theme}) => {
    return {
      container: {
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
      },
      infoContainer: {display: 'flex', flexDirection: 'row', marginTop: 40, marginLeft: 22},
      infoItemContainer: {
        marginLeft: 0,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 600,
        maxWidth: 600,
        width: 600,
      },
      imageContainer: {marginRight: 41},
      h2: {
        ...theme.fonts.h2,
      },
      divider: {
        width: '100%',
        height: 1,
        backgroundColor: theme.color.iconGrayColor,
        marginTop: 20,
      },
      bottomOffset: {
        marginBottom: theme.screen.bottomOffset,
      },
      FIO: {
        ...theme.fonts.body,
        color: theme.color.labelText,
      },
    };
  });
  if (!profile) return null;
  return (
    <LoadableContent loading={isLoading}>
      <View testID='profile-container-id' style={styles.container}>
        <Text testID='profile-label-id' style={styles.h2}>
          Профиль
        </Text>
        <View style={styles.infoContainer}>
          <HolderAvatar size={160} style={styles.imageContainer} />
          <View style={styles.infoItemContainer}>
            <Text testID='profile-label-id' style={styles.FIO}>
              ФИО
            </Text>
            <NameInfoDesktop testID='testIDS' name={profile.displayName!} />
            <InfoItem testID='fullname-birthday-id' title='Дата рождения' value={datePipe(profile.birthDate)} />
            <InfoItem
              testID='fullname-gender-id'
              title='Пол'
              value={profile.sex === Sex.male ? 'Мужской' : 'Женский'}
            />
            <View style={styles.divider} />
            <DataVisibilitySwitch isMasked={isMasked} onChange={setMasked} />
            <InfoItem
              testID='fullname-snils-id'
              title='СНИЛС'
              value={
                isMasked
                  ? maskPrivateData(formatSnils(profile.snils), {
                      maskType: PrivateDataMaskType.digits,
                    })
                  : formatSnils(profile.snils)
              }
            />
            <View style={styles.divider} />
            <View style={styles.bottomOffset}>
              <LinkedAccounts />
            </View>
          </View>
        </View>
      </View>
    </LoadableContent>
  );
};
